import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { 
  TrendingUp, 
  DollarSign, 
  Users, 
  BarChart3,
  CheckCircle,
  ArrowRight,
  Target,
  Star,
  BookOpen,
  Zap,
  Award,
  Heart,
  Crown,
  Eye,
  Briefcase,
  Coffee,
  Baby,
  User,
  ChefHat
} from 'lucide-react';

const BestKDPNiches2025Page = () => {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Top performing niches with data
  const topNiches = [
    {
      title: "Romance Fiction",
      icon: <Heart className="h-6 w-6" />,
      searchVolume: "2.4M",
      competition: "High",
      profitability: "Excellent",
      avgRoyalty: "$2,500",
      description: "Consistently the highest-earning fiction genre on KDP",
      tips: ["Focus on specific sub-genres", "Build series for loyal readers", "Engaging covers are crucial"],
      pseoLink: "/romance-book-cover-generator.html"
    },
    {
      title: "Self-Help & Personal Development", 
      icon: <Target className="h-6 w-6" />,
      searchVolume: "1.8M",
      competition: "Medium",
      profitability: "Excellent",
      avgRoyalty: "$3,200",
      description: "High-value niche with strong recurring readership",
      tips: ["Address specific problems", "Include actionable advice", "Build authority through expertise"],
      pseoLink: "/self-help-book-book-creator.html"
    },
    {
      title: "Business & Entrepreneurship",
      icon: <Briefcase className="h-6 w-6" />,
      searchVolume: "1.2M", 
      competition: "Medium",
      profitability: "Very Good",
      avgRoyalty: "$2,800",
      description: "Professional audience willing to pay premium prices",
      tips: ["Focus on current trends", "Use case studies", "Target specific industries"],
      pseoLink: "/business-book-cover-generator.html"
    },
    {
      title: "Fantasy Fiction",
      icon: <Crown className="h-6 w-6" />,
      searchVolume: "980K",
      competition: "High",
      profitability: "Very Good", 
      avgRoyalty: "$2,100",
      description: "Devoted fanbase with high engagement rates",
      tips: ["Create compelling worlds", "Develop series potential", "Invest in professional covers"],
      pseoLink: "/how-to-write-fantasy-books.html"
    },
    {
      title: "Mystery & Thriller",
      icon: <Eye className="h-6 w-6" />,
      searchVolume: "750K",
      competition: "Medium",
      profitability: "Good",
      avgRoyalty: "$1,900",
      description: "Strong market with loyal readers seeking new authors",
      tips: ["Master pacing and suspense", "Create memorable characters", "Hook readers early"],
      pseoLink: "/mystery-book-book-creator.html"
    },
    {
      title: "Cookbooks & Food",
      icon: <ChefHat className="h-6 w-6" />,
      searchVolume: "520K",
      competition: "Low",
      profitability: "Good",
      avgRoyalty: "$1,600",
      description: "Evergreen content with practical value",
      tips: ["Focus on specific diets/cuisines", "Include high-quality photos", "Test all recipes"],
      pseoLink: "/cookbook-book-cover-generator.html"
    },
    {
      title: "Children's Books",
      icon: <Baby className="h-6 w-6" />,
      searchVolume: "890K",
      competition: "Medium",
      profitability: "Good",
      avgRoyalty: "$1,400",
      description: "Gift market and educational sales boost profits",
      tips: ["Age-appropriate content", "Colorful, engaging illustrations", "Educational value"],
      pseoLink: "/children-book-cover-generator.html"
    },
    {
      title: "Biography & Memoir",
      icon: <User className="h-6 w-6" />,
      searchVolume: "340K",
      competition: "Low",
      profitability: "Good",
      avgRoyalty: "$1,800",
      description: "Lower competition with dedicated readership",
      tips: ["Focus on inspiring stories", "Thorough research required", "Emotional connection"],
      pseoLink: "/how-to-write-biography-books.html"
    }
  ];

  // Debug: Log the topNiches array
  console.log('BestKDPNiches2025 - topNiches count:', topNiches.length);
  console.log('BestKDPNiches2025 - topNiches titles:', topNiches.map(n => n.title));

  const emergingNiches = [
    "AI & Technology", "Cryptocurrency & NFTs", "Remote Work Guides", 
    "Mental Health", "Sustainability", "Digital Nomad Lifestyle"
  ];

  const monthlyTrends = [
    { month: "January", focus: "New Year Self-Improvement" },
    { month: "February", focus: "Romance (Valentine's Day)" },
    { month: "March", focus: "Business & Productivity" },
    { month: "April", focus: "Health & Fitness" },
    { month: "May", focus: "Parenting & Family" },
    { month: "June", focus: "Travel & Adventure" },
    { month: "July", focus: "Summer Romance" },
    { month: "August", focus: "Back-to-School Education" },
    { month: "September", focus: "Professional Development" },
    { month: "October", focus: "Horror & Mystery" },
    { month: "November", focus: "Holiday Cookbooks" },
    { month: "December", focus: "Gift Books & Children's" }
  ];

  const pageTitle = "Best KDP Niches 2025: Top 8 Profitable Book Categories for Amazon Publishing";
  const pageDescription = "Discover the most profitable KDP niches for 2025. Data-driven analysis of top book categories, competition levels, and earning potential for Amazon self-publishers.";
  const pageKeywords = "best KDP niches 2025, profitable book niches, Amazon KDP categories, self publishing niches, KDP book ideas, profitable book genres";

  // Article data for enhanced SEO
  const articleData = {
    author: "AI BookMoney Research Team",
    publishedTime: "2024-12-25T00:00:00Z",
    modifiedTime: "2024-12-25T00:00:00Z",
    section: "Publishing Strategy",
    tags: ["KDP niches", "book publishing", "Amazon KDP", "self publishing", "profitable niches", "2025 trends"]
  };

  // Schema markup for SEO
  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": pageTitle,
    "description": pageDescription,
    "author": {
      "@type": "Organization",
      "name": "AI BookMoney"
    },
    "publisher": {
      "@type": "Organization", 
      "name": "AI BookMoney",
      "logo": {
        "@type": "ImageObject",
        "url": "https://aibookmoney.com/logo.svg"
      }
    },
    "datePublished": "2024-12-25T00:00:00Z",
    "dateModified": "2024-12-25T00:00:00Z",
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": "https://aibookmoney.com/best-kdp-niches-2025"
    },
    "articleSection": "Publishing Strategy",
    "keywords": pageKeywords
  };

  return (
    <>
      <SEOHead 
        title={pageTitle}
        description={pageDescription}
        keywords={pageKeywords}
        canonical="/best-kdp-niches-2025"
        ogTitle="Best KDP Niches 2025: Top 8 Profitable Book Categories"
        ogDescription="Data-driven analysis of the most profitable KDP niches for 2025. Find your perfect book category with competition analysis and earning potential."
        ogImage="/kdp-niches-guide.png"
        ogUrl="/best-kdp-niches-2025"
        articleData={articleData}
        schemaData={schemaMarkup}
      />

      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900">
        <Navbar />

        {/* Hero Section */}
        <div className="pt-20 pb-12 px-4">
          <div className="max-w-6xl mx-auto">
            <div className="text-center mb-16">
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
                Best KDP Niches 2025
              </h1>
              <p className="text-xl md:text-2xl text-gray-300 mb-8 max-w-4xl mx-auto">
                Discover the most profitable book categories for Amazon KDP publishing. Data-driven analysis of competition, search volume, and earning potential.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
                <Link
                  to="/generate"
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 flex items-center"
                >
                  <Zap className="h-5 w-5 mr-2" />
                  Start Creating Your Book
                  <ArrowRight className="h-5 w-5 ml-2" />
                </Link>
                
                <div className="flex items-center text-gray-300">
                  <Users className="h-5 w-5 mr-2" />
                  <span>Updated December 2024 • Based on 50K+ books analyzed</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Top 8 Profitable Niches */}
        <section className="py-16 px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-4">
              Top 8 Most Profitable KDP Niches for 2025
            </h2>
            <p className="text-lg text-gray-300 text-center mb-12 max-w-3xl mx-auto">
              Based on market research, competition analysis, and author earnings data
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {topNiches.map((niche, index) => (
                <div key={index} className="bg-gray-800/50 rounded-2xl p-8 border border-purple-500/20 hover:border-purple-500/50 transition-all duration-300">
                  <div className="flex items-center mb-6">
                    <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-full p-3 mr-4">
                      {niche.icon}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold text-white">{niche.title}</h3>
                      <p className="text-purple-300">#{index + 1} Most Profitable</p>
                    </div>
                  </div>
                  
                  <p className="text-gray-300 mb-6">{niche.description}</p>
                  
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="bg-gray-900/50 rounded-lg p-3">
                      <div className="text-sm text-gray-400">Monthly Searches</div>
                      <div className="text-lg font-bold text-green-400">{niche.searchVolume}</div>
                    </div>
                    <div className="bg-gray-900/50 rounded-lg p-3">
                      <div className="text-sm text-gray-400">Avg. Monthly Royalty</div>
                      <div className="text-lg font-bold text-green-400">{niche.avgRoyalty}</div>
                    </div>
                    <div className="bg-gray-900/50 rounded-lg p-3">
                      <div className="text-sm text-gray-400">Competition</div>
                      <div className={`text-lg font-bold ${niche.competition === 'High' ? 'text-red-400' : niche.competition === 'Medium' ? 'text-yellow-400' : 'text-green-400'}`}>
                        {niche.competition}
                      </div>
                    </div>
                    <div className="bg-gray-900/50 rounded-lg p-3">
                      <div className="text-sm text-gray-400">Profit Potential</div>
                      <div className="text-lg font-bold text-green-400">{niche.profitability}</div>
                    </div>
                  </div>
                  
                  <div className="mb-6">
                    <h4 className="text-lg font-semibold text-white mb-3">Success Tips:</h4>
                    <ul className="space-y-2">
                      {niche.tips.map((tip, tipIndex) => (
                        <li key={tipIndex} className="flex items-start text-gray-300 text-sm">
                          <CheckCircle className="h-4 w-4 text-green-400 mr-2 mt-0.5 flex-shrink-0" />
                          {tip}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  <a
                    href={niche.pseoLink}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white py-3 px-6 rounded-lg font-semibold transition-all duration-300 transform hover:scale-105 flex items-center justify-center"
                  >
                    Start Creating {niche.title.split(' ')[0]} Books
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Monthly Trends */}
        <section className="py-16 px-4 bg-gray-900/50">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              Seasonal KDP Trends Calendar 2025
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {monthlyTrends.map((trend, index) => (
                <div key={index} className="bg-gray-800/50 rounded-xl p-6 border border-purple-500/20">
                  <h3 className="text-lg font-bold text-white mb-2">{trend.month}</h3>
                  <p className="text-purple-300 text-sm">{trend.focus}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Emerging Niches */}
        <section className="py-16 px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
              Emerging Niches to Watch in 2025
            </h2>
            
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {emergingNiches.map((niche, index) => (
                <div key={index} className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-lg p-4 text-center border border-purple-500/30">
                  <p className="text-white font-semibold text-sm">{niche}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="bg-gradient-to-r from-purple-800 to-pink-800 rounded-2xl p-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Ready to Dominate Your Chosen Niche?
              </h2>
              <p className="text-xl text-gray-200 mb-8">
                Use our AI-powered tools to create professional books in any of these profitable niches
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <Link
                  to="/generate"
                  className="bg-white text-purple-900 font-bold py-4 px-8 rounded-xl hover:bg-gray-100 transition-all duration-300 transform hover:scale-105 flex items-center"
                >
                  <BookOpen className="h-5 w-5 mr-2" />
                  Start Creating Your Book
                </Link>
                
                <Link
                  to="/kdp-covers"
                  className="border-2 border-white text-white font-bold py-4 px-8 rounded-xl hover:bg-white hover:text-purple-900 transition-all duration-300 flex items-center"
                >
                  <Star className="h-5 w-5 mr-2" />
                  Design Your Cover
                </Link>
              </div>
              
              <p className="text-gray-300 text-sm mt-4">
                No credit card required • 14-day free trial • Cancel anytime
              </p>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default BestKDPNiches2025Page;